import {
  KeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
} from 'react'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'

import {KEY_CODES} from 'definitions/constant'
import {DopamineResizeControls} from './useDopamineResizableWidth'

const RESIZE_STEP_PX = 16
const RESIZE_LARGE_STEP_PX = 48

type DopamineAssistantResizeHandleProps = {
  ariaControls: string
  /** Position and size; the handle brings its own cursor and focus ring. */
  className: string
  dataCy: string
  label: string
  resize: DopamineResizeControls
}

/**
 * A keyboard-operable separator that drags a width. Shared by the past chats
 * sidebar and the panel edge; pointer capture keeps the drag on the handle.
 * No tooltip on purpose: the edge draws its own line through ::after, which a
 * CSS tooltip would share, and the width is announced through aria-valuetext.
 */
export const DopamineAssistantResizeHandle = ({
  ariaControls,
  className,
  dataCy,
  label,
  resize,
}: DopamineAssistantResizeHandleProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const valueText = t(
    resize.width === null ? 'DOPAMINE_WIDTH_DEFAULT' : 'DOPAMINE_WIDTH',
    {width: resize.currentWidth},
  )

  const handlePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ): void => {
    if (event.button !== 0) return

    event.preventDefault()
    // Capture keeps move and release events on the handle even when the
    // pointer leaves it. jsdom does not implement capture.
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId)
    }
    resize.beginDrag(event.clientX)
  }

  const handlePointerMove = (
    event: ReactPointerEvent<HTMLDivElement>,
  ): void => {
    if (resize.isResizing) resize.drag(event.clientX)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = event.shiftKey ? RESIZE_LARGE_STEP_PX : RESIZE_STEP_PX

    switch (event.key) {
      case KEY_CODES.ARROW_RIGHT:
        resize.setWidth(resize.currentWidth + step)
        break
      case KEY_CODES.ARROW_LEFT:
        resize.setWidth(resize.currentWidth - step)
        break
      case KEY_CODES.END:
        resize.setWidth(resize.limits.max)
        break
      case KEY_CODES.HOME:
        resize.setWidth(resize.limits.min)
        break
      case KEY_CODES.ENTER:
        // Enter restores the default position, as the separator pattern suggests.
        resize.reset()
        break
      default:
        return
    }

    event.preventDefault()
  }

  return (
    <div
      aria-controls={ariaControls}
      aria-label={label}
      aria-orientation="vertical"
      aria-valuemax={resize.limits.max}
      aria-valuemin={resize.limits.min}
      aria-valuenow={resize.currentWidth}
      aria-valuetext={valueText}
      className={classNames(
        'cursor-col-resize touch-none focus:outline-none',
        className,
      )}
      data-cy={dataCy}
      role="separator"
      tabIndex={0}
      onDoubleClick={resize.reset}
      onKeyDown={handleKeyDown}
      onLostPointerCapture={resize.endDrag}
      onPointerCancel={resize.endDrag}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={resize.endDrag}
    />
  )
}
